/**
 * Updates the recently viewed products in localStorage.
 *
 * 저장 포맷: `[{ id: string, viewedAt: number }, ...]` (최신순).
 * `viewedAt` 은 저장 시점의 epoch ms 이고, 30일이 지난 항목은 읽는 시점에
 * 목록에서 제거된 뒤 다시 저장된다(노출 + 저장 모두 만료분 제외).
 *
 * 이전 포맷(`['123', '456']` — id 문자열 배열)은 읽을 때 현재 시각으로
 * 스탬프해 마이그레이션한다. 저장 시점을 알 수 없으므로 만료 기준은
 * 마이그레이션 시점부터 30일이다.
 *
 * ── 판독 게이트 ──
 * 최근 본 상품은 개인화라 preferences 동의 대상이고, ePrivacy 5조 3항은 저장과 판독을
 * 함께 규율한다 — 저장(snippets/scripts.liquid)뿐 아니라 판독도 막아야 한다.
 * 호출처가 제각각 GBConsent 를 다루지 않도록 `whenReadAllowed` · `onReadDenied` 로 묶었다.
 * 상세: docs/CONSENT.md
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in local storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 100;
  /** @static @constant {number} 저장 시점부터의 유효 기간 (30일, ms) */
  static #TTL_MS = 30 * 24 * 60 * 60 * 1000;
  /**
   * @static @constant {number} 동의 확정을 기다리는 기본 상한 (ms).
   *
   * Shopify 동의 API 가 끝내 로드되지 않으면 GBConsent 는 확정되지 않은 채 전 카테고리
   * 거부로 남는다(consent-signal 의 POLL_MAX 소진). 상한이 없으면 그 상태에서 호출처가
   * 영영 깨어나지 않아 화면이 로딩도 빈 상태도 아닌 채로 멈춘다.
   */
  static #READ_CONSENT_TIMEOUT_MS = 1000;
  /**
   * @static GBConsent 최초 확정을 기다리는 프라미스. `onChange` 는 해제 수단이 없어
   * 호출할 때마다 걸면 콜백이 쌓이므로 한 번만 만들고 재사용한다.
   * @type {Promise<void> | null}
   */
  static #consentResolution = null;

  /**
   * 최근 본 상품을 **판독해도 되는지** 판정한다. 저장소를 읽기 전에 반드시 통과시킨다.
   *
   * 확정 전이면(동의 API 로딩 중) 확정될 때까지 기다린다 — 안 기다리면 동의한 이용자가
   * 페이지 로드 직후 진입했을 때 목록이 조용히 비어 보인다. 다만 무한정 기다리지는 않는다.
   *
   * @param {object} [options]
   * @param {number} [options.timeoutMs] 확정 대기 상한(ms). 기본 1000.
   * @returns {Promise<boolean>} 판독 허용 여부. 거부·미확정·시그널 없음은 모두 false.
   */
  static async whenReadAllowed({ timeoutMs = this.#READ_CONSENT_TIMEOUT_MS } = {}) {
    const consent = window.GBConsent;

    /* 시그널이 없으면(스니펫 미렌더) 판독하지 않는다 — fail-closed. */
    if (!consent) {
      return false;
    }
    if (consent.isResolved()) {
      return consent.isAllowed('preferences');
    }

    this.#consentResolution ??= new Promise((resolve) => {
      consent.onChange(() => resolve());
    });

    await Promise.race([this.#consentResolution, new Promise((resolve) => setTimeout(resolve, timeoutMs))]);

    return consent.isAllowed('preferences');
  }

  /**
   * 판독이 거부됐을 때 호출할 콜백을 등록한다. 처음부터 거부든 세션 중 철회든 같이 받는다.
   * 이미 거부로 확정돼 있으면 등록 즉시 불린다.
   *
   * 저장된 데이터 삭제는 여기서 하지 않는다 — `assets/tracking.js` 의 `PREFERENCES_LOCAL_KEYS`
   * 가 철회 정리의 단일 지점이다. 콜백은 화면만 책임진다.
   *
   * @param {() => void} callback 거부·철회 시 실행할 정리 (보통 UI 숨김)
   */
  static onReadDenied(callback) {
    window.GBConsent?.onChange((status) => {
      if (status.preferences) {
        return;
      }
      callback();
    });
  }

  /**
   * Adds a product to the recently viewed products list.
   * 이미 있는 id 는 제거하고 맨 앞에 최신 시각으로 다시 넣는다(a → b → a 케이스).
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const id = String(productId);
    if (!id) {
      return;
    }

    const entries = this.#readEntries().filter((entry) => entry.id !== id);
    entries.unshift({ id, viewedAt: Date.now() });

    this.#writeEntries(entries.slice(0, this.#MAX_PRODUCTS));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from local storage.
   * 만료(30일 초과)된 항목은 제외하며, 제외된 게 있으면 정리된 목록을 다시 저장한다.
   * @returns {string[]} The list of viewed product ids, newest first.
   */
  static getProducts() {
    return this.#readEntries().map((entry) => entry.id);
  }

  /**
   * 저장된 항목을 읽어 만료분을 제거한다. 정리되거나 포맷이 바뀌었으면 즉시 다시 저장한다.
   * @returns {{ id: string, viewedAt: number }[]} 유효한 항목 (최신순)
   */
  static #readEntries() {
    /** @type {unknown} */
    let raw;
    try {
      raw = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      raw = [];
    }
    if (!Array.isArray(raw)) {
      raw = [];
    }

    const now = Date.now();
    let changed = false;

    /** @type {{ id: string, viewedAt: number }[]} */
    const entries = [];
    const seen = new Set();

    for (const item of /** @type {unknown[]} */ (raw)) {
      // 이전 포맷(id 문자열)은 현재 시각으로 스탬프해 마이그레이션한다.
      const entry =
        typeof item === 'string'
          ? { id: item, viewedAt: now }
          : { id: String(/** @type {any} */ (item)?.id ?? ''), viewedAt: Number(/** @type {any} */ (item)?.viewedAt) };

      if (typeof item === 'string') {
        changed = true;
      }

      // 잘못된 항목 / 중복 / 만료분은 버린다.
      if (!entry.id || !Number.isFinite(entry.viewedAt) || seen.has(entry.id) || now - entry.viewedAt >= this.#TTL_MS) {
        changed = true;
        continue;
      }

      seen.add(entry.id);
      entries.push(entry);
    }

    if (entries.length > this.#MAX_PRODUCTS) {
      entries.length = this.#MAX_PRODUCTS;
      changed = true;
    }

    if (changed) {
      this.#writeEntries(entries);
    }

    return entries;
  }

  /**
   * @param {{ id: string, viewedAt: number }[]} entries
   */
  static #writeEntries(entries) {
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // 저장 실패(용량 초과 / 프라이빗 모드)는 조회를 막지 않는다.
    }
  }
}
